<template>
  <OurModal v-model="isShownModal" :title="$t('title.fleaMarket.filters')" mobile-footer-reverse>
    <FilterForm
      v-model="form"
      :is-applications="isApplications"
      :regions="regions"
      :managers="managers"
      :sources="sources"
      :campaigns="campaigns"
      :mediums="mediums"
    />

    <template #footer-left>
      <OurButton :text="$t('button.apply')" @click="onClickApplyButton" />

      <OurButton :text="$t('button.close')" variant="secondary" @click="onClickCloseButton" />
    </template>

    <template #footer-right>
      <OurButton
        class="button"
        :text="$t('button.clear')"
        variant="thirdary"
        @click="onClickCleanButton"
      />
    </template>
  </OurModal>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";
import FilterForm from "./components/FilterForm";
import filterMixin from "@/mixins/filter.mixin";
import { regionsManagersAndUtmOptionsQuery } from "@/views/settings/dealership-applications/details/gql/queries";
import apolloMixin from "@/mixins/apollo.mixin";

const createApplicationsDefaultForm = (includeUtm = true) => ({
  applicationId: "",
  companyCode: "",
  taxNumber: "",
  phone: "",
  email: "",
  fullName: "",
  statuses: [],
  regionIds: [],
  ...(includeUtm
    ? {
        utmSources: [],
        utmCampaigns: [],
        utmMediums: [],
      }
    : {}),
  managerIds: [],
  date: {},
  withoutManager: false,
});

const createAnalyticsDefaultForm = (managerId) => ({
  managerIds: managerId ? [`${managerId}`] : [],
  date: {},
});

export default {
  name: "FilterModal",
  components: {
    FilterForm,
  },
  mixins: [filterMixin, apolloMixin],

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    isApplications: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      form: createApplicationsDefaultForm(),
      regions: [],
      managers: [],
      sources: [],
      campaigns: [],
      mediums: [],
    };
  },

  computed: {
    ...mapState("dealershipApplications", ["applicationsFiltersForm", "analyticsFiltersForm"]),
    ...mapGetters("user", ["userProfile", "getPermission"]),
    isShownModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },

    hasUtmPermission() {
      return this.getPermission["customer.applications_utm"];
    },
  },

  watch: {
    isShownModal: "onChangeShownModal",

    "userProfile.managerId": "onUserManagerIdChange",
  },

  created() {
    this.form = this.getDefaultForm();
    this.getQueryParam();
    this.applyAnalyticsManagerDefaultIfNeeded();
    this.setFiltersForm();
    this.getRegionsAndManagers();
  },

  beforeDestroy() {
    this.SET_FILTERS_FORM({
      isApplications: this.isApplications,
      form: this.cloneForm(this.form),
    });
  },

  methods: {
    ...mapMutations("dealershipApplications", ["SET_FILTERS_FORM"]),

    getDefaultForm() {
      return this.isApplications
        ? createApplicationsDefaultForm(this.hasUtmPermission)
        : createAnalyticsDefaultForm(this.userProfile?.managerId);
    },

    getMultiSelectQueryParams() {
      if (!this.isApplications) {
        return ["managerIds"];
      }

      const params = ["statuses", "regionIds"];

      if (this.hasUtmPermission) {
        params.push("utmSources", "utmCampaigns", "utmMediums");
      }

      params.push("managerIds");

      return params;
    },

    getBooleanQueryParams() {
      if (this.isApplications) {
        return ["withoutManager"];
      }

      return [];
    },

    cloneForm(form = this.getDefaultForm()) {
      const nextForm = {
        ...this.getDefaultForm(),
        ...form,
        date: form?.date ? { ...form.date } : {},
      };

      this.getMultiSelectQueryParams().forEach((key) => {
        nextForm[key] = Array.isArray(form?.[key]) ? [...form[key]] : [];
      });

      if (this.isApplications) {
        nextForm.withoutManager = Boolean(form?.withoutManager);

        if (!this.hasUtmPermission) {
          delete nextForm.utmSources;
          delete nextForm.utmCampaigns;
          delete nextForm.utmMediums;
        }
      }

      return nextForm;
    },

    onChangeShownModal() {
      if (this.isShownModal) {
        if (
          (this.isApplications && this.applicationsFiltersForm) ||
          (!this.isApplications && this.analyticsFiltersForm)
        ) {
          this.getForm();
        }
      } else {
        this.cleanForm();
      }
    },

    getForm() {
      const form = this.isApplications ? this.applicationsFiltersForm : this.analyticsFiltersForm;

      this.form = this.cloneForm(form);
    },

    cleanForm() {
      this.form = this.getDefaultForm();
      this.cleanQueryParam();
    },

    closeModal() {
      this.isShownModal = false;
      this.cleanForm();
    },

    onClickCloseButton() {
      this.closeModal();
    },

    onClickApplyButton() {
      this.setQueryParam();
      this.setFiltersForm();
      this.closeModal();
    },

    onClickCleanButton() {
      this.cleanForm();
    },

    setFiltersForm() {
      const form = this.cloneForm(this.form);

      this.SET_FILTERS_FORM({ isApplications: this.isApplications, form });
    },

    applyAnalyticsManagerDefaultIfNeeded() {
      const managerId = this.userProfile?.managerId;

      if (this.isApplications || !managerId || this.$route.query.managerIds) {
        return;
      }

      if (this.form.managerIds?.length) {
        return;
      }

      this.form = this.cloneForm({
        ...this.form,
        managerIds: [`${managerId}`],
      });
    },

    onUserManagerIdChange() {
      this.applyAnalyticsManagerDefaultIfNeeded();
      this.setFiltersForm();
    },

    normalizeManagers(managers = []) {
      return managers.map((manager) => ({
        ...manager,
        id: `${manager.id}`,
      }));
    },

    async getRegionsAndManagers() {
      const {
        regions,
        dealershipApplicationFilterManagers,
        dealershipApplicationFilterUtmSources,
        dealershipApplicationFilterUtmCampaigns,
        dealershipApplicationFilterUtmMediums,
      } = await this.$get(regionsManagersAndUtmOptionsQuery);

      this.regions = regions || [];
      this.managers = this.normalizeManagers(dealershipApplicationFilterManagers);
      this.sources = dealershipApplicationFilterUtmSources || [];
      this.campaigns = dealershipApplicationFilterUtmCampaigns || [];
      this.mediums = dealershipApplicationFilterUtmMediums || [];

      if (!this.isApplications && this.form.managerIds?.length) {
        this.form = this.cloneForm(this.form);
      }
    },
  },
};
</script>
